import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Drawer, IconButton, Tab, Tabs, CircularProgress, Alert,
  Chip, Divider, Stack
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Tooltip from '@mui/material/Tooltip';

import { fetchActiveFormulation } from '../../../services/gen2ReasoningApi';

const SectionRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" fontWeight={600}>{label}</Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{value}</Typography>
    </Box>
  );
};

const DiffDisplay = ({ diff }) => {
  if (!diff || !diff.hasChanges) {
    return <Typography variant="body2" color="text.secondary">No changes from the previous version.</Typography>;
  }
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        v{diff.prevVersion ?? '—'} → v{diff.currVersion}
      </Typography>
      {Object.entries(diff.sections).map(([key, section]) => (
        <Box key={key} sx={{ mb: 1.5 }}>
          <Typography variant="caption" fontWeight={600}>{section.label}</Typography>
          {section.changeType === 'added' && (
            <Typography variant="body2" sx={{ color: 'success.dark' }}>+ {section.after}</Typography>
          )}
          {section.changeType === 'removed' && (
            <Typography variant="body2" sx={{ color: 'error.dark', textDecoration: 'line-through' }}>− {section.before}</Typography>
          )}
          {section.changeType === 'changed' && (
            <>
              <Typography variant="body2" sx={{ color: 'error.dark', textDecoration: 'line-through' }}>− {section.before}</Typography>
              <Typography variant="body2" sx={{ color: 'success.dark' }}>+ {section.after}</Typography>
            </>
          )}
          {section.added?.map((item, i) => (
            <Typography key={`a-${i}`} variant="body2" sx={{ color: 'success.dark' }}>
              + {item.description || item.process || JSON.stringify(item)}
              {item.domain && <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>({item.domain})</Typography>}
            </Typography>
          ))}
          {section.removed?.map((item, i) => (
            <Typography key={`r-${i}`} variant="body2" sx={{ color: 'error.dark', textDecoration: 'line-through' }}>
              − {item.description || item.process || JSON.stringify(item)}
            </Typography>
          ))}
          {section.changed?.map((item, i) => (
            <Typography key={`c-${i}`} variant="body2">~ {item.description || item.process || JSON.stringify(item)}</Typography>
          ))}
        </Box>
      ))}
    </Box>
  );
};

const FormulationPanel = ({ open, onClose, clientId, token, gen2ApiBase = 'therapist' }) => {
  const [tab, setTab] = useState(0);
  const [formulation, setFormulation] = useState(null);
  const [history, setHistory] = useState([]);
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!clientId) return;
    try {
      setLoading(true);
      const data = await fetchActiveFormulation(clientId, token, gen2ApiBase);
      setFormulation(data.formulation || null);
      setHistory(data.history || []);
      setDiff(data.diff || null);
      setError('');
    } catch (err) {
      console.error('[FormulationPanel] load error:', err);
      setError(err?.response?.data?.msg || 'Could not load formulation.');
    } finally {
      setLoading(false);
    }
  }, [clientId, token, gen2ApiBase]);

  useEffect(() => { if (open) load(); }, [open, load]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="temporary"
      ModalProps={{ keepMounted: true }}
      PaperProps={{ sx: { width: { xs: '100%', sm: 460 }, p: 0, display: 'flex', flexDirection: 'column' } }}
    >
      {/* Sticky header so the close button stays reachable regardless of scroll */}
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 1,
        backgroundColor: 'background.paper',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        p: 2, borderBottom: '1px solid', borderColor: 'divider'
      }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Clinical Formulation</Typography>
          {formulation && (
            <Typography variant="caption" color="text.secondary">
              v{formulation.version} · {formulation.updateTrigger?.replace(/_/g, ' ') || 'unknown trigger'}
            </Typography>
          )}
        </Box>
        <Tooltip title="Close (Esc)">
          <IconButton onClick={onClose} aria-label="Close formulation" sx={{ ml: 1 }}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
        <Tab label="Current" sx={{ textTransform: 'none' }} />
        <Tab label="Diff" sx={{ textTransform: 'none' }} />
        <Tab label="History" sx={{ textTransform: 'none' }} />
      </Tabs>

      <Box sx={{ p: 2, overflow: 'auto', flexGrow: 1 }}>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loading && !formulation && (
          <Typography variant="body2" color="text.secondary">
            No formulation has been created for this client yet. The Gen 2 caregiver builds the initial formulation in the background after the first session.
          </Typography>
        )}

        {!loading && formulation && tab === 0 && (
          <Box>
            {formulation.narrativeSummary && (
              <>
                <Typography variant="subtitle2" gutterBottom>Summary</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>{formulation.narrativeSummary}</Typography>
                <Divider sx={{ mb: 2 }} />
              </>
            )}

            {formulation.presentingProblems?.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" fontWeight={600}>Presenting problems</Typography>
                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                  {formulation.presentingProblems.map((p, i) => (
                    <Box key={i}>
                      <Typography variant="body2">{p.description}</Typography>
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.25 }}>
                        {p.domain && <Chip label={p.domain} size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />}
                        {p.severity && <Chip label={p.severity} size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            <SectionRow label="Predisposing factors"  value={formulation.predisposingFactors} />
            <SectionRow label="Precipitating factors" value={formulation.precipitatingFactors} />
            <SectionRow label="Perpetuating factors"  value={formulation.perpetuatingFactors} />
            <SectionRow label="Protective factors"    value={formulation.protectiveFactors} />

            {formulation.transdiagnosticProcesses?.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" fontWeight={600}>Transdiagnostic processes</Typography>
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', mt: 0.5 }}>
                  {formulation.transdiagnosticProcesses.map((p, i) => (
                    <Chip
                      key={i}
                      label={`${p.process} · ${p.intensity}`}
                      size="small"
                      variant="outlined"
                      sx={{ mb: 0.5 }}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {formulation.currentApproach && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" fontWeight={600}>Current approach</Typography>
                <Typography variant="body2">
                  {formulation.currentApproach.primaryModality || '—'}
                </Typography>
                {formulation.currentApproach.rationale && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                    {formulation.currentApproach.rationale}
                  </Typography>
                )}
                {formulation.currentApproach.activeModules?.length > 0 && (
                  <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', mt: 0.5 }}>
                    {formulation.currentApproach.activeModules.map((m, i) => (
                      <Chip key={i} label={m} size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                    ))}
                  </Stack>
                )}
              </Box>
            )}

            <SectionRow label="Measurement summary" value={formulation.measurementSummary} />
          </Box>
        )}

        {!loading && tab === 1 && (
          <DiffDisplay diff={diff} />
        )}

        {!loading && tab === 2 && (
          <Box>
            {history.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No earlier versions.</Typography>
            ) : (
              history.map((h, i) => (
                <Box key={i} sx={{ mb: 1.5, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="body2">v{h.version}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {new Date(h.createdAt).toLocaleString()}
                  </Typography>
                  {h.updateTrigger && (
                    <Chip label={h.updateTrigger.replace(/_/g, ' ')} size="small" variant="outlined" sx={{ height: 18, fontSize: 10, mt: 0.5 }} />
                  )}
                </Box>
              ))
            )}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default FormulationPanel;
